import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import styled, { keyframes } from "styled-components";

import FetchModule from "../../../Model/Network/FetchModule";

import { getSampleBoardData } from "../Model/BoardSampleData";

import RankBadge1 from '../../../Image/Board/RankBadge1.svg';
import RankBadge2 from '../../../Image/Board/RankBadge2.svg';
import RankBadge3 from '../../../Image/Board/RankBadge3.svg';
import LinkoSymbol from '../../../Image/Board/LinkoSymbol.svg';
import LinkoSymbolGray from '../../../Image/Board/LinkoSymbolGray.svg';
import LinkoLogo from '../../../Image/Board/LinkoLogo.png';
import LinkoLoadingLogo from '../../../Image/Board/LinkoLoadingLogo.png';

// 자동 갱신 주기. 화면 클릭 또는 Space 로 수동 갱신하면 그 시점부터 다시 카운트된다 //
const POLLING_INTERVAL = 600000;

// 디자인 원본 캔버스 크기 (보드 1개 기준) //
const BOARD_WIDTH = 3840;
const BOARD_HEIGHT = 2160;

const DEFAULT_BOARD_COUNT = 3;
const RANK_ROW_ANIMATION_DELAY = 150;

// 키보드 레이아웃(한글 입력 상태)과 무관하게 인식되도록 code 로 판별한다 //
const FULL_SCREEN_KEY_CODE = 'Enter';
const REFRESH_KEY_CODE = 'Space';

// 데이터가 먼저 도착해도 로고 펄스를 최소 횟수만큼 재생한 뒤 보드를 노출한다 //
const LOGO_PULSE_DURATION = 1500;
const MINIMUM_PULSE_COUNT = 3;
const MINIMUM_LOADING_DURATION = LOGO_PULSE_DURATION * MINIMUM_PULSE_COUNT;

// 타이틀 원본은 Industry Black 이나 상용 폰트라 보유중인 Pretendard Black 으로 대체한다 //
const TITLE_FONT = 'Pretendard-Black';
const BOLD_FONT = 'Pretendard-Bold';
const MEDIUM_FONT = 'Pretendard-Medium';
const REGULAR_FONT = 'Pretendard-Regular';

// 순위 숫자와 기록 단위 라벨은 디자인이 Noto Sans KR 로 지정되어 있어 별도로 둔다 //
const RANK_NUMBER_FONT = 'NotoSansKR-Bold';
const UNIT_FONT = 'NotoSansKR-Regular';

// 화면에 표시할 보드 개수 결정 //
function decideBoardCount(boardData, wod) {
  if (boardData !== undefined) return boardData.boards.length;
  else if (wod !== undefined) return 1;
  else return DEFAULT_BOARD_COUNT;
}

export default function EventBoardPage(props) {
  const { isSample } = props;

  const params = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [boardData, setBoardData] = useState(undefined);

  const [scale, setScale] = useState(1);
  const [refreshCount, setRefreshCount] = useState(0);

  const HasDataRef = useRef(false);
  const LoadingStartTimeRef = useRef(Date.now());

  // 보드 데이터 불러오기 (10분 주기 자동 갱신, 화면 클릭 또는 Space 로 즉시 갱신) //
  useEffect(() => {
    let revealTimer = undefined;

    // 로고 펄스 최소 재생 시간을 채운 뒤 보드를 노출 //
    function completeLoading() {
      if (revealTimer !== undefined) return;

      const remainTime = MINIMUM_LOADING_DURATION - (Date.now() - LoadingStartTimeRef.current);

      if (remainTime <= 0) {
        setIsLoading(false);
        return;
      }

      revealTimer = setTimeout(() => setIsLoading(false), remainTime);
    }

    function fetchBoardData() {
      // 현장 안내용 예시 보드는 서버를 조회하지 않고 고정 데이터를 표시한다 //
      if (isSample) {
        const sampleData = getSampleBoardData(params.wod);

        if (sampleData === undefined) {
          setIsError(true);
          setIsLoading(false);
          return;
        }

        setBoardData(sampleData);
        setIsError(false);
        HasDataRef.current = true;
        completeLoading();

        return;
      }

      const fetchModule = new FetchModule();
      (
        async () => {
          let middleURL = 'public/board/' + params.organizerId + '/' + params.region;

          if (params.wod !== undefined) middleURL += '/' + params.wod;

          // 네트워크 단절시 fetch 가 예외를 던지므로 직전 데이터를 유지하도록 감싼다 //
          try {
            const responseData = await fetchModule.getDataOfEventPublicVer2(middleURL, 'GET');

            if (responseData.status === 200) {
              setBoardData(responseData.data);
              setIsError(false);
              HasDataRef.current = true;
              completeLoading();
            }
            else if (!HasDataRef.current) {
              setIsError(true);
              setIsLoading(false);
            }
            else completeLoading();
          }
          catch (e) {
            if (!HasDataRef.current) {
              setIsError(true);
              setIsLoading(false);
            }
            else completeLoading();
          }
        }
      )();
    }

    fetchBoardData();

    // 이전에 받아둔 데이터가 있으면 응답이 늦거나 실패해도 최소 시간 뒤 직전 순위를 다시 노출한다 //
    if (HasDataRef.current) completeLoading();

    const pollingTimer = isSample ? undefined : setInterval(fetchBoardData, POLLING_INTERVAL);

    return () => {
      clearInterval(pollingTimer);
      clearTimeout(revealTimer);
    };
  }, [isSample, params.organizerId, params.region, params.wod, refreshCount])

  // 화면 크기에 맞춰 캔버스 배율 계산 //
  useEffect(() => {
    function onResizeWindow() {
      const canvasWidth = BOARD_WIDTH * decideBoardCount(boardData, params.wod);
      const widthScale = window.innerWidth / canvasWidth;
      const heightScale = window.innerHeight / BOARD_HEIGHT;

      setScale(Math.min(widthScale, heightScale));
    }

    onResizeWindow();
    window.addEventListener('resize', onResizeWindow);
    document.addEventListener('fullscreenchange', onResizeWindow);

    return () => {
      window.removeEventListener('resize', onResizeWindow);
      document.removeEventListener('fullscreenchange', onResizeWindow);
    };
  }, [boardData, params.wod])

  // 단축키 처리 (Enter: 전체화면 전환 / Space: 갱신) //
  useEffect(() => {
    function onKeyDownWindow(e) {
      if (e.code === FULL_SCREEN_KEY_CODE) {
        e.preventDefault();

        if (document.fullscreenElement === null) document.documentElement.requestFullscreen().catch(() => {});
        else document.exitFullscreen().catch(() => {});

        return;
      }

      if (e.code !== REFRESH_KEY_CODE) return;

      e.preventDefault();

      LoadingStartTimeRef.current = Date.now();

      setIsLoading(true);
      setRefreshCount((previousCount) => previousCount + 1);
    }

    window.addEventListener('keydown', onKeyDownWindow);

    return () => window.removeEventListener('keydown', onKeyDownWindow);
  }, [])

  // 화면 클릭시 즉시 갱신 (로딩 화면부터 다시 시작) //
  function onClickBoard() {
    LoadingStartTimeRef.current = Date.now();

    setIsLoading(true);
    setRefreshCount((previousCount) => previousCount + 1);
  }

  // 표시할 값이 비어있는지 확인 (숫자 0 은 유효값으로 취급) //
  function isEmptyValue(value) {
    return value === null || value === undefined || value === '';
  }

  // 기록 문자열 안의 단위를 라벨 스타일로 분리해 렌더링 (5R 12Reps → 숫자 / 단위) //
  function renderRecordText(record) {
    const tokens = record.match(/[0-9:.]+|[^0-9:.]+/g);

    if (tokens === null) return record;

    return tokens.map((token, index) => {
      if (/^[0-9:.]+$/.test(token)) return token;

      return <RecordUnitText key={'RecordUnit-' + index}>{token}</RecordUnitText>;
    });
  }

  // 타이브레이크 렌더링 (동점시 순위를 가르는 기준값) //
  function renderTieBreak(tieBreak) {
    if (isEmptyValue(tieBreak)) return null;

    return (
      <TieBreakWrapper>
        <TieBreakValueText>{tieBreak}</TieBreakValueText>
        <RecordTypeText>TieBreak</RecordTypeText>
      </TieBreakWrapper>
    );
  }

  // 순위별 리본 이미지 결정 //
  function decideRankBadgeImage(rank) {
    if (rank === 1) return RankBadge1;
    else if (rank === 2) return RankBadge2;
    else return RankBadge3;
  }

  // 순위 뱃지 렌더링 (1~3위 리본 / 4위 이하 사각형) //
  function renderRankBadge(rank) {
    if (rank > 3) {
      return (
        <RankNumberBox>
          <RankNumberText>{rank}</RankNumberText>
        </RankNumberBox>
      );
    }

    return (
      <RankBadgeWrapper>
        <RankBadgeImage src={decideRankBadgeImage(rank)} />
        <RankBadgeNumberText>{rank}</RankBadgeNumberText>
      </RankBadgeWrapper>
    );
  }

  // 프로필 이미지 로드 실패시 기본 이미지로 대체 //
  function onErrorProfileImage(e) {
    e.target.onerror = null;
    e.target.src = LinkoSymbol;
    e.target.style.backgroundColor = '#1D1D1D';
    e.target.style.objectFit = 'contain';
    e.target.style.padding = '27px';
  }

  // 선수 프로필 렌더링 //
  function renderProfile(profile) {
    if (profile === undefined || profile === '') {
      return (
        <DefaultProfileWrapper>
          <DefaultProfileImage src={LinkoSymbol} />
        </DefaultProfileWrapper>
      );
    }

    return (
      <ProfileImage
        src={profile}
        onError={onErrorProfileImage} />
    );
  }

  // 순위 행 렌더링 //
  function renderRankRow(data, rowIndex) {
    return (
      <RankRowWrapper
        key={'RankRow-' + refreshCount + '-' + rowIndex}
        $delay={rowIndex * RANK_ROW_ANIMATION_DELAY}>
        {renderRankBadge(data.rank)}
        <RecordCardWrapper>
          {renderProfile(data.profile)}
          <RecordInfoWrapper>
            <NameText>{data.name}</NameText>
            <RecordWrapper>
              <RecordText>{renderRecordText(data.record)}</RecordText>
              {
                !isEmptyValue(data.recordType) &&
                <RecordTypeText>{data.recordType}</RecordTypeText>
              }
              {
                !isEmptyValue(data.point) &&
                <RecordText>{data.point}<RecordUnitText>point</RecordUnitText></RecordText>
              }
              {renderTieBreak(data.tieBreak)}
            </RecordWrapper>
          </RecordInfoWrapper>
        </RecordCardWrapper>
      </RankRowWrapper>
    );
  }

  // 기록이 없는 리그 렌더링 //
  function renderEmptyLeague() {
    return (
      <EmptyLeagueWrapper>
        <EmptyLeagueSymbolImage src={LinkoSymbolGray} />
      </EmptyLeagueWrapper>
    );
  }

  // 리그 컬럼 렌더링 //
  function renderLeagueColumn(league, leagueIndex) {
    return (
      <LeagueColumnWrapper key={'LeagueColumn-' + leagueIndex}>
        <LeagueHeaderWrapper>
          <LeagueNameText>{league.leagueName}</LeagueNameText>
        </LeagueHeaderWrapper>
        {
          league.ranks.length === 0 &&
          renderEmptyLeague()
        }
        {
          league.ranks.length > 0 &&
          league.ranks.map((data, index) => renderRankRow(data, index))
        }
      </LeagueColumnWrapper>
    );
  }

  // 와드 보드 1개 렌더링 //
  function renderBoard(board, boardIndex) {
    return (
      <BoardScaleWrapper
        key={'Board-' + boardIndex}
        $scale={scale}>
        <BoardWrapper $scale={scale}>
          <BoardContentWrapper>
            <WodTitleWrapper>
              <WodTitleText>{board.wodTitle}</WodTitleText>
            </WodTitleWrapper>
            <LeaguesWrapper>
              {board.leagues.map((league, index) => renderLeagueColumn(league, index))}
            </LeaguesWrapper>
            <FooterWrapper>
              <PoweredByText>powered by</PoweredByText>
              <FooterLogoImage src={LinkoLogo} />
            </FooterWrapper>
          </BoardContentWrapper>
        </BoardWrapper>
      </BoardScaleWrapper>
    );
  }

  // 로딩 화면 렌더링 (보드 개수만큼 동일하게 표시) //
  function renderLoadingView() {
    const loadingBoards = [];

    for (let i = 0; i < decideBoardCount(boardData, params.wod); i++) {
      loadingBoards.push(
        <BoardScaleWrapper
          key={'LoadingBoard-' + i}
          $scale={scale}>
          <BoardWrapper $scale={scale}>
            <LoadingLogoImage src={LinkoLoadingLogo} />
          </BoardWrapper>
        </BoardScaleWrapper>
      );
    }

    return loadingBoards;
  }

  // 조회 실패 화면 렌더링 //
  function renderErrorView() {
    return (
      <BoardScaleWrapper $scale={scale}>
        <BoardWrapper $scale={scale}>
          <ErrorWrapper>
            <ErrorText>보드 정보를 불러오지 못했습니다</ErrorText>
            <ErrorSubText>주소를 확인하거나 화면을 클릭해 다시 시도해주세요</ErrorSubText>
          </ErrorWrapper>
        </BoardWrapper>
      </BoardScaleWrapper>
    );
  }

  return (
    <TopLevelWrapper onClick={onClickBoard}>
      <CanvasWrapper>
        {
          isError ?
            renderErrorView()
            :
            isLoading ?
              renderLoadingView()
              :
              boardData.boards.map((board, index) => renderBoard(board, index))
        }
      </CanvasWrapper>
    </TopLevelWrapper>
  );
}

const rankRowEnterAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const logoPulseAnimation = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const TopLevelWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  width: 100vw;
  height: 100vh;

  overflow: hidden;

  background-color: #0A0A0A;

  user-select: none;

  :hover {
    cursor: pointer;
  }
`;

const CanvasWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  align-items: center;
`;

// 캔버스를 통째로 축소하면 통합 보드 좌표 공간이 11520px 가 되어 브라우저 최대 텍스처 크기(8192px) 밖의 애니메이션이 그려지지 않는다 //
// 보드마다 따로 축소해 좌표 공간을 3840px 로 유지한다 //
const BoardScaleWrapper = styled.div`
  flex-shrink: 0;

  width: ${props => BOARD_WIDTH * props.$scale}px;
  height: ${props => BOARD_HEIGHT * props.$scale}px;

  overflow: hidden;
`;

const BoardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  width: ${BOARD_WIDTH}px;
  height: ${BOARD_HEIGHT}px;

  background-color: #0A0A0A;

  transform: scale(${props => props.$scale});
  transform-origin: top left;
`;

const BoardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;

  gap: 20px;

  box-sizing: border-box;

  width: 3440px;
  height: 1847px;

  padding-bottom: 10px;
`;

const WodTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  align-items: center;
  justify-content: center;

  width: 100%;
  height: 402px;
`;

const WodTitleText = styled.p`
  font-family: ${TITLE_FONT};
  font-size: 300px;

  color: #FFFFFF;

  white-space: nowrap;

  margin: 0 0 0 0;
`;

const LeaguesWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  gap: 100px;

  width: 100%;
  height: 1095px;
`;

const LeagueColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;

  gap: 20px;

  width: 1080px;
  height: 100%;
`;

const LeagueHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100px;

  border-radius: 20px 20px 0px 0px;

  background-color: #1D1D1D;
`;

const LeagueNameText = styled.p`
  font-family: ${BOLD_FONT};
  font-size: 60px;

  color: #FFFFFF;

  margin: 0 0 0 0;
`;

const RankRowWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  align-items: flex-start;

  gap: 30px;

  box-sizing: border-box;

  width: 100%;
  height: 179px;

  padding: 5px 0px 5px 20px;

  background-color: #0A0A0A;

  animation: ${rankRowEnterAnimation} 450ms cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: ${props => props.$delay}ms;
`;

const RankBadgeWrapper = styled.div`
  position: relative;

  flex-shrink: 0;

  width: 100px;
  height: 100px;
`;

const RankBadgeImage = styled.img`
  display: block;

  width: 100px;
  height: 100px;
`;

const RankBadgeNumberText = styled.p`
  position: absolute;
  top: 0;
  left: 0;

  display: flex;

  align-items: center;
  justify-content: center;

  width: 100px;
  height: 71.25px;

  font-family: ${RANK_NUMBER_FONT};
  font-size: 65px;

  color: #FFFFFF;

  margin: 0 0 0 0;
`;

const RankNumberBox = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  align-items: center;
  justify-content: center;

  width: 100px;
  height: 100px;

  border-radius: 12px;

  background-color: #818181;
`;

const RankNumberText = styled.p`
  font-family: ${RANK_NUMBER_FONT};
  font-size: 65px;

  color: #0A0A0A;

  margin: 0 0 0 0;
`;

const RecordCardWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex: 1;

  align-items: center;

  gap: 20px;

  box-sizing: border-box;

  min-width: 0;
  height: 169px;

  padding: 12px;

  border-radius: 12px;

  overflow: hidden;

  background-color: #111111;
`;

const ProfileImage = styled.img`
  flex-shrink: 0;

  box-sizing: border-box;

  width: 145px;
  height: 145px;

  border-radius: 12px;

  object-fit: cover;

  background-color: #FFFFFF;
`;

const DefaultProfileWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  align-items: center;
  justify-content: center;

  width: 145px;
  height: 145px;

  border-radius: 12px;

  background-color: #1D1D1D;
`;

const DefaultProfileImage = styled.img`
  width: 90px;
  height: 90px;
`;

const RecordInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;

  justify-content: center;

  min-width: 0;
`;

const NameText = styled.p`
  font-family: ${MEDIUM_FONT};
  font-size: 60px;

  line-height: 72px;

  color: #FFFFFF;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  margin: 0 0 0 0;
`;

const RecordWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;

  min-width: 0;
  height: 72px;

  overflow: hidden;
`;

const RecordText = styled.p`
  flex-shrink: 0;

  font-family: ${MEDIUM_FONT};
  font-size: 60px;

  line-height: 72px;

  color: #FFFFFF;

  white-space: nowrap;

  margin: 0 0 0 0;
`;

const RecordTypeText = styled.p`
  flex-shrink: 0;

  font-family: ${UNIT_FONT};
  font-size: 40px;

  line-height: 72px;

  color: #818181;

  white-space: nowrap;

  margin: 0 0 0 0;
`;

const TieBreakWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex: 0 1 auto;

  align-items: center;

  gap: 10px;

  min-width: 0;
`;

const TieBreakValueText = styled.p`
  font-family: ${UNIT_FONT};
  font-size: 40px;

  line-height: 72px;

  color: #B4B4B4;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  margin: 0 0 0 0;
`;

const RecordUnitText = styled.span`
  font-family: ${UNIT_FONT};
  font-size: 40px;

  color: #818181;
`;

const EmptyLeagueWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex: 1;

  align-items: center;
  justify-content: center;
`;

const EmptyLeagueSymbolImage = styled.img`
  width: 600px;
  height: 600px;
`;

const FooterWrapper = styled.div`
  display: flex;
  flex-direction: row;

  flex-shrink: 0;

  align-items: center;
  justify-content: center;

  gap: 20px;

  width: 100%;
  height: 300px;
`;

const PoweredByText = styled.p`
  font-family: ${REGULAR_FONT};
  font-size: 90px;

  color: #FFFFFF;

  margin: 0 0 0 0;
`;

const FooterLogoImage = styled.img`
  width: 298px;
  height: 131.35px;
`;

const LoadingLogoImage = styled.img`
  width: 2002px;
  height: 882.44px;

  animation: ${logoPulseAnimation} 1500ms ease-in-out infinite;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  gap: 40px;

  width: 100%;
  height: 100%;
`;

const ErrorText = styled.p`
  font-family: ${MEDIUM_FONT};
  font-size: 120px;

  color: #FFFFFF;

  margin: 0 0 0 0;
`;

const ErrorSubText = styled.p`
  font-family: ${REGULAR_FONT};
  font-size: 70px;

  color: #818181;

  margin: 0 0 0 0;
`;
